import { C3OrderedProductStatus } from "../enums/c3-ordered-product-status.enum";
import { C3Order } from "./c3-order.model";
import { C3ProductVersion } from "./c3-product-version.model";
import { Customer } from "./customer.model";

/**
 * A single line on a C3 order. Unlike PlasticsOrderedProduct there are no
 * production/inventory plan flags or scheduled-release weeks. The line carries
 * its product, quantity, due/status details, notes, audit history, and the exact
 * released graphic revision selected for every unit on that line.
 */
export interface C3OrderedProduct {
    id: string;
    c3OrderId: string;
    productId: string;
    quantity: number;
    dueDate: Date;
    salesOrderNumber: string;
    status: C3OrderedProductStatus;

    /** Stable graphic-catalog identity selected for every unit on this line. */
    graphicId?: string;
    /** Exact released artwork revision; later catalog revisions do not alter the order. */
    graphicVersionId?: string;

    /** Free-text note on the line. */
    note: string;
    /**
     * Spanish translation of `note`, filled in automatically whenever the note changes.
     * C3 has no production note, so unlike plastics the translation tracks `note` itself.
     */
    translatedNote: string;

    createdById: string;
    createdOn: Date;
    updatedById: string;
    updatedOn: Date;

    /** Append-only audit of edits, newest last. Shown behind the row expander. */
    changeLog: C3OrderedProductChangeLog[];
}

export interface C3OrderedProductChangeLog {
    id: string;
    createdOn: Date;
    createdById: string;
    text: string;
}

export interface DisplayC3OrderedProduct {
    orderedProduct: C3OrderedProduct;
    orderedProductVersion: C3ProductVersion;
    order: C3Order;
    /** null when the order's customer has been deleted — the line still renders. */
    orderCustomer: Customer | null;
}

export function toDisplayC3OrderedProducts(
    orderedProducts: C3OrderedProduct[],
    productIdToVersionDict: Record<string, C3ProductVersion>,
    orderDict: Record<string, C3Order>,
    customerDict: Record<string, Customer>,
): DisplayC3OrderedProduct[] {
    return orderedProducts.flatMap((orderedProduct) => {
        const order = orderDict[orderedProduct.c3OrderId];
        const orderedProductVersion = productIdToVersionDict[orderedProduct.productId];

        if (!order || !orderedProductVersion) {
            return [];
        }

        return [{
            orderedProduct,
            orderedProductVersion,
            order,
            orderCustomer: customerDict[order.customerId] ?? null,
        }];
    });
}
